import styles from "../AddMarkerModal/AddIncidentModal.module.css";
import React, { useEffect, useMemo, useReducer } from "react";
import {
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { useFetchAllTypeContainerPlatforms } from "../../../../hooks/guide/useFetchAllTypeContainerPlatforms";

import useAppSnackbar from "../../../../hooks/useAppSnackbar";
import { useFetchContainerPlatformsById } from "../../../../hooks/guide/useFetchContainerPlatformsById";
import { useFetchAllTypeContainer } from "../../../../hooks/guide/useFetchAllTypeContainer";
import {
  ContainerInCPDto,
  UpdateContainerPlatform,
} from "../../../../types/api/guide/containerPlatform.types";
import { containerPlatformService } from "../../../../services/guide/containerPlatform-service";
import { useFetchAllCounterparty } from "../../../../hooks/guide/useFetchAllCounterparty";
import { ScheduleBuildResult } from "../../../../components/ScheduleBuilder/ScheduleBuilder";
import { useFetchAllSchedules } from "../../../../hooks/planRoute/useFetchAllSchedules";
import { useFetchScheduleByCpId } from "../../../../hooks/planRoute/useFetchScheduleByCpId";
import { UpdatePlanCp } from "../../../../types/api/planRoute/cp.types";
import { cpService } from "../../../../services/planRoute/cp-service";

type Guid = string;

type ContainerInCPForm = {
  uiId: string; // локальный идентификатор для React key
  volume: number | "";
  kind: string;
  typeContainerId: Guid;
};

type ContainerPlatformFormState = {
  id: Guid;
  name: string;
  address: string;
  typeContainerPlatformsId: Guid;
  containers: ContainerInCPForm[];
  counterpartyId: string;
  schedule?: ScheduleBuildResult | null;
  scheduleName?: string;
  scheduleId?: string;
};

type Action =
  | {
      type: "SET_FIELD";
      name: keyof Omit<ContainerPlatformFormState, "containers">;
      value: any;
    }
  | {
      type: "SET_CONTAINER_FIELD";
      uiId: string;
      name: keyof Omit<ContainerInCPForm, "uiId">;
      value: any;
    }
  | { type: "ADD_CONTAINER" }
  | { type: "REMOVE_CONTAINER"; uiId: string }
  | { type: "SET_FORM"; payload: Partial<ContainerPlatformFormState> }
  | { type: "SET_SCHEDULE"; value: ScheduleBuildResult };

const createUiId = () =>
  Math.random().toString(16).slice(2) + Date.now().toString(16);

const reducer = (
  state: ContainerPlatformFormState,
  action: Action,
): ContainerPlatformFormState => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.name]: action.value };

    case "SET_CONTAINER_FIELD":
      return {
        ...state,
        containers: state.containers.map((c) =>
          c.uiId === action.uiId ? { ...c, [action.name]: action.value } : c,
        ),
      };

    case "ADD_CONTAINER":
      return {
        ...state,
        containers: [
          ...state.containers,
          {
            uiId: createUiId(),
            volume: "",
            kind: "",
            typeContainerId: "" as Guid,
          },
        ],
      };

    case "REMOVE_CONTAINER":
      return {
        ...state,
        containers: state.containers.filter((c) => c.uiId !== action.uiId),
      };

    case "SET_FORM":
      return { ...state, ...action.payload };

    case "SET_SCHEDULE":
      return { ...state, schedule: action.value };
    default:
      return state;
  }
};

interface EditContainerPlatformsModalProps {
  id: string;
  onClose: () => void;
  refetchData: () => void;
}

export default function EditContainerPlatformsModal({
  id,
  onClose,
  refetchData,
}: EditContainerPlatformsModalProps) {
  const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();

  const { data: typesCP = [] } = useFetchAllTypeContainerPlatforms();
  const { data: typeContainers = [] } = useFetchAllTypeContainer();
  const { data: counterparties = [] } = useFetchAllCounterparty();
  const { data: schedules = [] } = useFetchAllSchedules();
  const { data: schedule } = useFetchScheduleByCpId(id);
  const { data: cp } = useFetchContainerPlatformsById(id);

  const initialState: ContainerPlatformFormState = {
    id,
    name: "",
    address: "",
    typeContainerPlatformsId: "" as Guid,
    containers: [],
    counterpartyId: "",
    scheduleId: "",
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!cp) return;
    dispatch({
      type: "SET_FORM",
      payload: {
        id: cp.id,
        containers: cp.containers.map((x: ContainerInCPDto) => ({
          uiId: createUiId(),
          volume: x.volume,
          kind: x.kind,
          typeContainerId: x.typeContainerId,
        })),
        name: cp.name,
        address: cp.address,
        typeContainerPlatformsId: cp.typeContainerPlatformsId,
        counterpartyId: cp.counterpartyId,
      },
    });
  }, [cp]);
  useEffect(() => {
    if (!schedule) return;
    dispatch({ type: "SET_FIELD", name: "scheduleId", value: schedule.id });
  }, [schedule]);

  const handleValue = (event: any) => {
    dispatch({
      type: "SET_FIELD",
      name: event.target.name,
      value: event.target.value,
    });
  };

  const handleContainerValue = (uiId: string) => (event: any) => {
    const { name, value } = event.target;

    // volume — число
    if (name === "volume") {
      const next = value === "" ? "" : Number(value);
      dispatch({
        type: "SET_CONTAINER_FIELD",
        uiId,
        name: "volume",
        value: next,
      });
      return;
    }

    dispatch({ type: "SET_CONTAINER_FIELD", uiId, name, value });
  };

  const canSave = useMemo(() => {
    if (!state.name.trim()) return false;
    if (!state.address.trim()) return false;
    if (!state.typeContainerPlatformsId) return false;

    if (state.containers.length === 0) return false;

    // базовая валидация контейнеров
    for (const c of state.containers) {
      if (c.volume === "" || Number(c.volume) <= 0) return false;
      if (!c.kind.trim()) return false;
      if (!c.typeContainerId) return false;
    }

    return true;
  }, [state]);

  const handleSave = async () => {
    try {
      const payload: UpdateContainerPlatform = {
        id: state.id,
        title: state.name.trim(),
        address: state.address.trim(),
        typeContainerPlatformsId: state.typeContainerPlatformsId,
        containerInCp: state.containers.map((c) => ({
          volume: Number(c.volume),
          kind: c.kind.trim(),
          typeContainerId: c.typeContainerId,
        })),
        counterpartyId: state.counterpartyId,
      };

      await containerPlatformService.update(state.id, payload);

      const planCp: UpdatePlanCp = {
        scheduleId: state.scheduleId,
        cpId: id,
      };
      await cpService.update(id, planCp);

      showSuccess("Сохрание прошло успешно");
      onClose();
      refetchData();
    } catch (e: any) {
      showError(e?.response?.data ?? "Ошибка сохранения");
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            Редактировать контейнерную площадку
          </h3>
          <button className={styles.modalClose} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Название</div>
            <div className={styles.modalCoords}>
              <TextField
                name="name"
                value={state.name}
                type="text"
                onChange={handleValue}
                fullWidth
              />
            </div>
          </div>

          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Адрес</div>
            <div className={styles.modalCoords}>
              <TextField
                name="address"
                value={state.address}
                type="text"
                onChange={handleValue}
                fullWidth
              />
            </div>
          </div>

          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Тип КП</div>
            <div className={styles.modalCoords}>
              <Select
                name="typeContainerPlatformsId"
                value={state.typeContainerPlatformsId}
                onChange={handleValue}
                fullWidth
                displayEmpty
                renderValue={(selected) =>
                  typesCP?.find((t: any) => t.id === selected)?.name ??
                  (selected ? String(selected) : "Выберите тип")
                }
                MenuProps={{
                  disablePortal: true,
                  PaperProps: { sx: { zIndex: 20000 } },
                }}
              >
                {typesCP.map((type: any) => (
                  <MenuItem key={type.id} value={type.id}>
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Контрагент</div>
            <div className={styles.modalCoords}>
              <Select
                name="counterpartyId"
                value={state.counterpartyId}
                onChange={handleValue}
                fullWidth
                displayEmpty
                renderValue={(selected) =>
                  counterparties?.find((t: any) => t.id === selected)?.name ??
                  (selected ? String(selected) : "Выберите контрагента")
                }
                MenuProps={{
                  disablePortal: true,
                  PaperProps: { sx: { zIndex: 20000 } },
                }}
              >
                {counterparties.map((counterparty: any) => (
                  <MenuItem key={counterparty.id} value={counterparty.id}>
                    <ListItemText primary={counterparty.name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          {/* Контейнеры */}
          <div className={styles.modalField}>
            <div
              className={styles.modalLabel}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Контейнеры</span>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => dispatch({ type: "ADD_CONTAINER" })}
              >
                Добавить
              </Button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {state.containers.length === 0 && (
                <div style={{ opacity: 0.7 }}>Контейнеров пока нет</div>
              )}

              {state.containers.map((c) => (
                <div
                  key={c.uiId}
                  style={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 8,
                    padding: 12,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr auto",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    name="kind"
                    label="Вид"
                    value={c.kind}
                    onChange={handleContainerValue(c.uiId)}
                    fullWidth
                  />

                  <TextField
                    name="volume"
                    label="Объем"
                    type="number"
                    value={c.volume}
                    onChange={handleContainerValue(c.uiId)}
                    fullWidth
                    inputProps={{ min: 0, step: "any" }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Тип контейнера</InputLabel>
                    <Select
                      name="typeContainerId"
                      value={c.typeContainerId}
                      onChange={handleContainerValue(c.uiId)}
                      label="Тип контейнера"
                      MenuProps={{
                        disablePortal: true,
                        PaperProps: { sx: { zIndex: 20000 } },
                      }}
                    >
                      {typeContainers.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <IconButton
                    aria-label="delete"
                    onClick={() =>
                      dispatch({ type: "REMOVE_CONTAINER", uiId: c.uiId })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <>
            <div className={styles.modalField}>
              <div className={styles.modalLabel}>Выберете график</div>
              <div className={styles.modalCoords}>
                <Select
                  name={"scheduleId"}
                  value={state?.scheduleId}
                  onChange={handleValue}
                  renderValue={(selected) =>
                    schedules?.find((schedule) => schedule.id === selected)
                      ?.name ?? ""
                  }
                  MenuProps={{
                    disablePortal: true,
                    PaperProps: { sx: { zIndex: 20000 } },
                  }}
                >
                  {schedules?.map((schedule) => (
                    <MenuItem key={schedule?.id} value={schedule?.id}>
                      <ListItemText primary={schedule?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.modalButton} onClick={onClose}>
            Отмена
          </button>
          <button
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            onClick={handleSave}
            disabled={!canSave}
            style={{ opacity: canSave ? 1 : 0.6 }}
          >
            Сохранить
          </button>
        </div>
      </div>

      <SnackbarComponent />
    </div>
  );
}
